import React, { Fragment, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography, useTheme } from '@mui/material';

import {
	StyledDonutChartBackSvg,
	StyledDonutChartBackground,
	StyledDonutChartSegment,
	StyledDonutChartTextValue,
	StyledDonutChartWrap,
} from './style';

export interface Segment {
	value: number;
	label: string;
	color: string;
}

export interface DonutChartProps {
	segments: Segment[];
	size?: number;
	strokeWidth?: number;
	gap?: number;
	combinedSegments?: number[][];
}

function DonutChart({
	segments,
	size = 64,
	strokeWidth = 16,
	gap = 3,
	combinedSegments = [],
}: DonutChartProps) {
	const theme = useTheme();
	const { t } = useTranslation();

	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;

	const totalValue = segments.reduce((acc, segment) => acc + segment.value, 0);
	const activeSegments = segments.filter((segment) => segment.value > 0);
	const singleSegment = activeSegments.length === 1;
	const gapLength = singleSegment ? 0 : (gap / 100) * circumference;

	// min length 3%
	const minSegmentLength = 0.03 * circumference;

	const minSegmentValue = (minSegmentLength / circumference) * totalValue;

	// new segments based on min value on 3%
	const adjustedSegments = segments.map((segment) =>
		segment.value > 0 && segment.value < minSegmentValue
			? { ...segment, adjustedValue: minSegmentValue }
			: { ...segment, adjustedValue: segment.value },
	);

	// total value with adjusted Segments
	const adjustedTotalValue = adjustedSegments.reduce(
		(acc, segment) => acc + segment.adjustedValue!,
		0,
	);

	let cumulativeValue = 0;

	// Check if segment is combined
	const isCombined = (index: number): boolean =>
		combinedSegments.some((group) => {
			const nonZeroIndices = group.filter(
				(i) => Boolean(segments[i]?.value) && segments[i]?.value > 0,
			);
			return (
				nonZeroIndices.includes(index) && nonZeroIndices.indexOf(index) < nonZeroIndices.length - 1
			);
		});

	return (
		<StyledDonutChartBackground>
			<StyledDonutChartWrap size={size}>
				<StyledDonutChartBackSvg width={size} height={size}>
					{adjustedTotalValue > 0 ? (
						<>
							{adjustedSegments.map((segment, index) => {
								const segmentValue = segment.adjustedValue;

								if (!segmentValue) return null;

								const segmentLength = (segmentValue / adjustedTotalValue) * circumference;
								const offset =
									circumference - (cumulativeValue / adjustedTotalValue) * circumference;

								const segmentLengthWithGap = isCombined(index)
									? segmentLength + 1
									: segmentLength - gapLength;

								const startAngle = (cumulativeValue / adjustedTotalValue) * 2 * Math.PI;
								cumulativeValue += segmentValue;
								const endAngle = (cumulativeValue / adjustedTotalValue) * 2 * Math.PI;
								const middleAngle = (startAngle + endAngle) / 2;

								const textRadius = radius;
								const middleX = size / 2 + textRadius * Math.cos(middleAngle);
								const middleY = size / 2 + textRadius * Math.sin(middleAngle);

								return (
									<Fragment key={segment.label}>
										<StyledDonutChartSegment
											stroke={segment.color}
											strokeWidth={strokeWidth}
											fill="none"
											r={radius}
											cx={size / 2}
											cy={size / 2}
											offset={offset - gapLength / 2}
											strokeDasharray={`${segmentLengthWithGap} ${
												circumference - segmentLengthWithGap
											}`}
										/>
										<g transform={`translate(${middleX},${middleY})`}>
											<StyledDonutChartTextValue
												x={0}
												y={0}
												textAnchor="middle"
												dominantBaseline="central"
												transform="rotate(90)"
											>
												{segment.value}
											</StyledDonutChartTextValue>
										</g>
									</Fragment>
								);
							})}
						</>
					) : (
						<StyledDonutChartSegment
							stroke={theme.palette.success.contrastText}
							strokeWidth={strokeWidth}
							fill="none"
							r={radius}
							cx={size / 2}
							cy={size / 2}
							offset={0}
							strokeDasharray={`${circumference} 0`}
						/>
					)}
				</StyledDonutChartBackSvg>
				<Typography variant="body1">{totalValue}</Typography>
				<Typography variant="body1">{t('program.totalLessons')}</Typography>
			</StyledDonutChartWrap>
		</StyledDonutChartBackground>
	);
}

export default memo(DonutChart);
